/**
 * @file components/audit-tasks/phases/PhasePlanning.tsx
 * @description 階段 2：稽核計劃。
 *
 * 內容：
 *  - 計畫月份顯示（由稽核員確認）
 *  - 預定稽核日期（設定 / 改期 / 歷史）與稽核人天數（2026-09-15 回報序28：由「回覆派案」移入，
 *    與稽核排程計劃放在一起並可修改）
 *  - 查核表清單（FormSelector）：按方案+任務類型自動預選默認模板，組長/管理員可手動增刪
 *  - 打包下載全部表單（客戶 PPT S40）：GET /api/audit-tasks/:taskId/forms/export.zip
 *
 * 完整 FormSelector 實現於 Task #9。本檔案目前依賴 ./FormSelector。
 */

import { useState } from 'react';
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd';
import { FolderArchive } from 'lucide-react';
import type { AuditTaskDetail } from '@zhcb/shared';
import { FormSelector } from '@/components/audit-tasks/FormSelector';
import { SchedulePlanCard } from '@/components/audit-tasks/SchedulePlanCard';
import { SchedulePanel } from '@/components/audit-tasks/SchedulePanel';
import { PersonDaysCard } from '@/components/audit-tasks/PersonDaysCard';
import { message } from '@/lib/antd-app';

interface Props {
  task: AuditTaskDetail;
  /** 任務詳情失效時觸發父層重新拉取（FormSelector 變更後） */
  onChanged?: () => void;
}

export function PhasePlanning({ task, onChanged }: Props) {
  /** zip 打包下載進行中標記（防重複點擊） */
  const [exporting, setExporting] = useState(false);

  /**
   * 打包下載該任務全部表單（PDF 優先，無 DOCX 模板的表單由後端回落 XLSX，
   * 失敗明細見 zip 內 EXPORT-NOTES.txt）。
   */
  const handleExportZip = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/api/audit-tasks/${task.id}/forms/export.zip`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        // 錯誤時後端回 JSON errorResponse；解析失敗則給通用文案
        const body = (await res.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;
        const code = body?.error?.code;
        message.error(code === 'NO_FORMS' ? '該任務尚未綁定任何表單' : '打包下載失敗，請稍後重試');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${task.planCode ?? `TASK-${task.id}`}-forms.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export forms zip failed', err);
      message.error('打包下載失敗，請檢查網絡後重試');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="排程資訊">
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="計畫月份">
            {task.plannedAuditMonth ? (
              <Tag color="blue">{task.plannedAuditMonth}</Tag>
            ) : (
              <Typography.Text type="secondary">尚未確認</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="任務狀態">{task.status}</Descriptions.Item>
        </Descriptions>
        {!task.plannedAuditMonth && (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}
          >
            請於下方「預定稽核日期」設定日期（計畫月份自動帶入），並填寫稽核人天數。
          </Typography.Paragraph>
        )}
      </Card>

      {/* 序28：預定稽核日期（設定 / 改期 / 歷史）+ 稽核人天數，與排程計劃放在一起 */}
      <SchedulePanel task={task} />
      <PersonDaysCard task={task} />

      {/* 序67/68/69：現場稽核日程（多天直觀表格）+ 抽樣計劃 + 前次不符合清單 */}
      <SchedulePlanCard task={task} />

      <Card
        size="small"
        title="查核表單"
        extra={
          <Button
            size="small"
            icon={<FolderArchive size={14} />}
            loading={exporting}
            onClick={() => void handleExportZip()}
          >
            打包下載全部表單
          </Button>
        }
      >
        <FormSelector task={task} onChanged={onChanged} />
      </Card>
    </Space>
  );
}
